import { useMemo } from 'react';
import {
  Employer,
  EmployerContact,
  EmployerSubscriptionFeedback,
  EmployerUserContact,
  EntityId,
  Invoice,
  Note,
  NoteCategory,
  NoteEntityCategory,
} from '@restworld/utility-types';
import { ENDPOINTS } from './../../endpoints';
import { Service } from './../base';
import dynamicAxios from './../../utils/axios';
import {
  EmployerFilterType,
  IndexEmployers,
  IndexInvoices,
  IndexStripePaymentMethods,
  IndexStripeSubscriptions,
  ShowEmployer,
  SubscriptionDuration,
  SubscriptionSession,
  GetAdPerformanceStatistics,
  ApplicationPerformanceMetrics,
  FetchSubscriptionFeedbackByOrganization,
} from './types';
import { Id, UploadPicture } from '../../utils/common_types';
import useServices from '../../hooks/useServices';
import { ReferralLinkResponse } from '../useAccounts/types';

class ServiceEmployers extends Service {
  fetchEmployers({
    limit,
    offset,
    order_by,
    filters,
  }: {
    limit: number;
    offset: number;
    order_by?: string[];
    filters?: EmployerFilterType;
  }) {
    return this.axiosInstance.get<IndexEmployers>(ENDPOINTS.employers.root, {
      params: { limit, offset, order_by, ...filters },
    });
  }

  showEmployer(employerId: EntityId) {
    return this.axiosInstance.get<ShowEmployer>(`${ENDPOINTS.employers.root}/${employerId}`);
  }

  searchCompanies({ searchText, countryCode }: { searchText?: string; countryCode: string }) {
    return this.axiosInstance.get(ENDPOINTS.employers.searchCompanies, {
      params: { search_text: searchText, country_code: countryCode },
    });
  }

  sendSubscriptionEmail({ employerId, paymentLink }: { employerId?: string; paymentLink?: string }) {
    return this.axiosInstance.post<string>(ENDPOINTS.employers.subscriptionEmail, {
      employer_id: employerId,
      payment_link: paymentLink,
    });
  }

  uploadPicture({ employerId, file }: { employerId: EntityId; file: File }) {
    const formData = new FormData();
    formData.append('user_id', employerId);
    formData.append('picture', file);

    return this.axiosInstance.post<UploadPicture>(ENDPOINTS.employers.picture, formData, {
      headers: {
        'content-type': 'multipart/form-data',
      },
    });
  }

  deletePicture(employerId: EntityId) {
    return this.axiosInstance.delete<string>(ENDPOINTS.employers.picture, {
      data: { user_id: employerId },
    });
  }

  fetchInvoices({
    limit,
    offset,
    employerId,
    organizationId,
    onlyPaid,
    csmId,
    cancelled,
  }: {
    limit: number;
    offset: number;
    employerId?: string;
    organizationId?: string;
    onlyPaid?: boolean;
    csmId?: string;
    cancelled?: boolean;
  }) {
    return this.axiosInstance.get<IndexInvoices>(ENDPOINTS.employers.invoices, {
      params: {
        employer_id: employerId,
        csm_id: csmId,
        limit,
        offset,
        'paid?': onlyPaid,
        'cancelled?': cancelled,
        organization_id: organizationId,
      },
    });
  }

  createInvoice({ invoice }: { invoice: Partial<Invoice> }) {
    return this.axiosInstance.post<Id>(ENDPOINTS.employers.invoices, {
      ...invoice,
    });
  }

  updateInvoice({ invoiceId, params }: { invoiceId: number; params: Partial<Invoice> }) {
    return this.axiosInstance.put<Id>(`${ENDPOINTS.employers.invoices}/${invoiceId}`, {
      ...params,
    });
  }

  fetchInvoicePdf({ invoiceId }: { invoiceId: number }) {
    return this.axiosInstance.get<any>(ENDPOINTS.employers.invoicePdf, {
      responseType: 'blob',
      params: {
        invoice_id: invoiceId,
      },
    });
  }

  fetchNotes({
    entityCategory,
    entityId,
    noteCategory,
    organizationId,
  }: {
    entityId: string;
    entityCategory: NoteEntityCategory;
    noteCategory?: NoteCategory;
    organizationId?: string;
  }) {
    return this.axiosInstance.get<Array<Note>>(ENDPOINTS.employers.notes, {
      params: {
        id: entityId,
        entity_category: entityCategory,
        category: noteCategory,
        organization_id: organizationId,
      },
    });
  }

  insertNote({
    entityCategory,
    entityId,
    note,
    category,
    jobPositionId,
    workerId,
    organizationId,
    jobApplicationId,
  }: {
    entityId?: string;
    entityCategory: NoteEntityCategory;
    note: string;
    category?: NoteCategory;
    jobPositionId?: string;
    workerId?: string;
    organizationId?: string;
    jobApplicationId?: string;
  }) {
    return this.axiosInstance.post<Id>(getEndpoint(entityCategory), {
      note,
      category,
      job_position_id: jobPositionId,
      worker_id: workerId,
      organization_id: organizationId,
      job_application_id: jobApplicationId,
      ...(entityId ? formatEntityId(entityCategory, entityId) : {}),
    });
  }

  updateNote({ note, noteId }: { noteId: number; note: string }) {
    return this.axiosInstance.put<string>(`${ENDPOINTS.employers.notes}/${noteId}`, { note });
  }

  removeNote(noteId: number) {
    return this.axiosInstance.delete<string>(`${ENDPOINTS.employers.notes}/${noteId}`);
  }

  indexSubscriptions(employerId: EntityId) {
    return this.axiosInstance.get<IndexStripeSubscriptions>(ENDPOINTS.employers.subscription, {
      params: { employer_id: employerId },
    });
  }

  createSubscriptionSession({
    duration,
    pricingType,
    slots,
    companyId,
  }: {
    companyId: string;
    duration: SubscriptionDuration;
    pricingType: 'old' | 'new';
    slots: number;
  }) {
    return this.axiosInstance.post<SubscriptionSession>(ENDPOINTS.employers.subscriptionSession, {
      company_id: companyId,
      duration,
      pricing_type: pricingType,
      slots,
    });
  }

  indexPaymentMethods(employerId: EntityId) {
    return this.axiosInstance.get<IndexStripePaymentMethods>(ENDPOINTS.employers.paymentMethod, {
      params: { employer_id: employerId },
    });
  }

  updateAssignation({ employerId, assignedTo }: { employerId: EntityId; assignedTo: string | undefined }) {
    return this.axiosInstance.post<string>(ENDPOINTS.employers.assignCustomerSuccessManager, {
      employer_id: employerId,
      assigned_to: assignedTo,
    });
  }

  updateEmployer({ employerId, params }: { employerId?: EntityId; params?: Partial<Employer> }) {
    return this.axiosInstance.put<string>(`${ENDPOINTS.employers.root}/${employerId}`, {
      ...(params || {}),
    });
  }

  createEmployerContact(employerContact: Partial<EmployerContact>) {
    return this.axiosInstance.post<Id>(ENDPOINTS.employers.employerContact, {
      ...(employerContact || {}),
    });
  }

  updateEmployerContact({
    employerContactId,
    params,
  }: {
    employerContactId: number;
    params: Partial<EmployerContact>;
  }) {
    return this.axiosInstance.patch<Id>(`${ENDPOINTS.employers.employerContact}/${employerContactId}`, {
      ...(params || {}),
    });
  }

  redirectStripeCustomerPortal() {
    return this.axiosInstance.get<string>(ENDPOINTS.employers.stripeCustomerPortal);
  }

  createCheckoutSession(invoiceId: number) {
    return this.axiosInstance.get<{ url: string }>(ENDPOINTS.employers.createCheckoutSession, {
      params: { invoice_id: invoiceId },
    });
  }

  getAccessLink(employerId?: EntityId) {
    return this.axiosInstance.get<string>(ENDPOINTS.employers.accessLink, {
      params: { employer_id: employerId },
    });
  }

  createEmployerSubscriptionFeedback(params: Partial<EmployerSubscriptionFeedback>) {
    return this.axiosInstance.post<string>(ENDPOINTS.employers.subscriptionFeedback, {
      ...params,
    });
  }

  fetchSubscriptionFeedbackByOrganization(params: { organization_id?: string }) {
    return this.axiosInstance.get<FetchSubscriptionFeedbackByOrganization[]>(
      ENDPOINTS.employers.fetchEmployerSubscriptionFeedbackByOrganization,
      {
        params,
      }
    );
  }

  cancelStripeSubscription({ stripeSubscriptionId }: { stripeSubscriptionId: string }) {
    return this.axiosInstance.post<string>(ENDPOINTS.employers.cancelStripeSubscription, {
      stripe_subscription_id: stripeSubscriptionId,
    });
  }

  getEmployerContacts = (organizationId: string) => {
    return this.axiosInstance.get<{
      employer_contacts: EmployerUserContact[];
    }>(ENDPOINTS.employers.employerContacts, {
      params: {
        organization_id: organizationId,
      },
    });
  };

  get_ad_performance_statistics = (jobPositionId: string) => {
    return this.axiosInstance.get<GetAdPerformanceStatistics>(ENDPOINTS.employers.adPerformanceStatistics, {
      params: {
        job_position_id: jobPositionId,
      },
    });
  };

  get_application_performance_metrics = (jobPositionId: string) => {
    return this.axiosInstance.get<ApplicationPerformanceMetrics>(ENDPOINTS.employers.applicationPerformance, {
      params: {
        job_position_id: jobPositionId,
      },
    });
  };

  is_subscription_feedbacks_submitted = (employerId?: string) => {
    return this.axiosInstance.get<boolean>(ENDPOINTS.employers.isSubscriptionFeedbacksSubmitted, {
      params: {
        employer_id: employerId,
      },
    });
  };

  getReferralLink = () => {
    return this.axiosInstance.get<ReferralLinkResponse>(ENDPOINTS.employers.referralLink);
  };
}

const formatEntityId = (category: string, entityId: string) => {
  return {
    [`${category}_id`]: entityId,
  };
};

const getEndpoint = (entityCategory: NoteEntityCategory) => {
  switch (entityCategory) {
    case 'worker':
      return ENDPOINTS.workers.notes;
    default:
      return ENDPOINTS.employers.notes;
  }
};

export function useEmployersService() {
  const { baseUrl, jwt } = useServices();
  return useMemo(() => new ServiceEmployers(dynamicAxios(baseUrl, jwt)), [baseUrl, jwt]);
}

export const createEmployerService = ({ baseUrl, jwt }: { baseUrl: string; jwt?: string }) =>
  new ServiceEmployers(dynamicAxios(baseUrl, jwt));

export default useEmployersService;

export * from './types';
